import { CloseButton, Popover } from '@mantine/core';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import IconArrowDown from '../../icons/IconArrowDown';
import styles from './styles.scoped.css';

function normalizeOptions(options) {
  return (options || []).map((option) => ({
    label: option?.label || option?.name || '',
    value: option?.value,
  }));
}

export default function ChipMultiSelect({
  className,
  error,
  maxValues,
  onChange,
  options,
  placeholder,
  value,
}) {
  const [opened, setOpened] = useState(false);

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedItems = useMemo(
    () =>
      normalizedOptions.filter((option) =>
        (value || []).some((selectedValue) => selectedValue === option.value)
      ),
    [normalizedOptions, value]
  );

  const classes = [
    styles.root,
    className,
    error ? styles.isError : '',
  ]
    .filter(Boolean)
    .join(' ');

  const toggleValue = (optionValue) => {
    const currentValues = value || [];
    const isSelected = currentValues.includes(optionValue);

    if (isSelected) {
      onChange(currentValues.filter((item) => item !== optionValue));
      return;
    }

    if (currentValues.length >= maxValues) {
      return;
    }

    onChange([...currentValues, optionValue]);
  };

  const handleRemove = (event, optionValue) => {
    event.stopPropagation();
    onChange((value || []).filter((item) => item !== optionValue));
  };

  return (
    <Popover
      closeOnClickOutside
      onChange={setOpened}
      opened={opened}
      position="bottom"
      shadow="md"
      width="target"
    >
      <Popover.Target>
        <button
          className={classes}
          onClick={() => setOpened((current) => !current)}
          type="button"
        >
          <div className={styles.valueArea}>
            {selectedItems.length > 0 ? (
              selectedItems.map((item) => (
                <span className={styles.chip} key={item.value}>
                  <span className={styles.chipLabel}>{item.label}</span>
                  <span className={styles.chipAction}>
                    <CloseButton
                      aria-label={`Hapus ${item.label}`}
                      iconSize={10}
                      onClick={(event) => handleRemove(event, item.value)}
                      size="sm"
                    />
                  </span>
                </span>
              ))
            ) : (
              <span className={styles.placeholder}>{placeholder}</span>
            )}
          </div>
          <IconArrowDown className={styles.chevron} />
        </button>
      </Popover.Target>

      <Popover.Dropdown className={styles.dropdown}>
        <div className={styles.optionList}>
          {normalizedOptions.map((option) => {
            const checked = (value || []).includes(option.value);
            const disabled = !checked && (value || []).length >= maxValues;

            return (
              <button
                className={[
                  styles.optionRow,
                  checked ? styles.optionRowSelected : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={disabled}
                key={option.value}
                onClick={() => toggleValue(option.value)}
                type="button"
              >
                <span className={styles.optionLabel}>{option.label}</span>
              </button>
            );
          })}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

ChipMultiSelect.defaultProps = {
  className: '',
  error: false,
  maxValues: 2,
  onChange: () => {},
  options: [],
  placeholder: 'Pilih Provinsi',
  value: [],
};

ChipMultiSelect.propTypes = {
  className: PropTypes.string,
  error: PropTypes.bool,
  maxValues: PropTypes.number,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  value: PropTypes.array,
};
